import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense } from "react";

// Layouts
import AuthLayout from "../components/layout/AuthLayout";
import MainLayout from "../components/layout/MainLayout";

// Pages
import NotFoundPage from "../pages/NotFoundPage";
import LoginPage from "../pages/auth/LoginPage";

// Store
import { useAppStore } from "../store/useAppStore";

// Configuración de rutas
import { routesConfig, flattenRoutes, type RouteConfig } from "../config/routes.config";

// Componente de carga
import Loading from "../components/shared/Loading";

// Componente para rutas protegidas
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    // Redirigir al login si no está autenticado
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Componente para rutas públicas (solo accesibles si NO estás autenticado)
const PublicRoute = ({ children }: ProtectedRouteProps) => {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    // Redirigir al dashboard si ya está autenticado
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

/**
 * Función para renderizar rutas dinámicamente desde la configuración
 */
const renderRoutes = (routes: RouteConfig[]) => {
  const flatRoutes = flattenRoutes(routes);

  return flatRoutes.map((route) => {
    const Component = route.component;

    if (!Component) return null;

    return (
      <Route
        key={route.id}
        path={route.path}
        element={
          <Suspense fallback={<Loading size="lg" text="Cargando página..." fullScreen />}>
            <Component />
          </Suspense>
        }
      />
    );
  });
};

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Rutas públicas (Auth) */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <AuthLayout>
                <LoginPage />
              </AuthLayout>
            </PublicRoute>
          }
        />

        {/* Rutas protegidas (requieren autenticación) */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Redirigir la raíz al dashboard */}
          <Route index element={<Navigate to="/dashboard" replace />} />

          {/* Renderizar todas las rutas desde la configuración */}
          {renderRoutes(routesConfig)}
        </Route>

        {/* Ruta 404 - Página no encontrada */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
